import type { AppSidebarSection } from "@civala/shared-ui"
import {
  IconBrain,
  IconBriefcase,
  IconDatabase,
  IconHierarchy,
  IconHome,
  IconMail,
  IconRobot,
  IconServer,
  IconSitemap,
} from "@tabler/icons-react"

/**
 * A portal nav section, plus the demo flag the shell filters on.
 *
 * `demo: true` means the screen exists and renders but its CONTENT IS OUT OF DATE — it
 * is hidden unless demo mode is switched on (see useDemoMode; default off). Distinct
 * from nexus's `Panel.demo`, which marks screens not yet built on live data. Remove the
 * flag when the page's data is current again; that is the whole lifecycle.
 *
 * Hidden sections keep working by direct URL — the flag governs the sidebar, not the
 * router, same as the unlisted Candidate Screener at /hr/screener.
 */
export type PortalNavSection = AppSidebarSection & {
  readonly demo?: boolean
}

// Flat top-level nav for the portal, mapped onto the shared AppSidebar shape.
// Home sits at "/" so its matchTarget is an exact "/" match (the shared
// AppSidebar special-cases "/" to avoid prefix-matching every route).
export const NAV_SECTIONS: ReadonlyArray<PortalNavSection> = [
  { id: "home", label: "Home", icon: IconHome, path: "/" },
  { id: "org-chart", label: "Org Chart", icon: IconHierarchy, path: "/org-chart", demo: true },
  { id: "agents", label: "AI Agents", icon: IconRobot, path: "/agents", demo: true },
  { id: "projects", label: "Projects", icon: IconBriefcase, path: "/projects", demo: true },
  { id: "skills", label: "Skills", icon: IconBrain, path: "/skills" },
  {
    id: "connectors",
    label: "Connectors",
    icon: IconServer,
    path: "/connectors",
    children: [{ id: "retrieval-paths", label: "Civala Corpus", path: "/connectors/retrieval-paths" }],
  },
  { id: "data-sources", label: "Data Sources", icon: IconDatabase, path: "/data-sources", demo: true },
  { id: "architecture", label: "Architecture", icon: IconSitemap, path: "/architecture" },
  // Candidate Screener (/hr/screener) is unlisted for now — the route and page
  // still exist and work by direct URL, it just isn't in the sidebar.
  { id: "signature", label: "Email Signature", icon: IconMail, path: "/it/signature" },
]

/** Route paths of every demo section — used to gate links elsewhere (e.g. the home catalog). */
export const DEMO_PATHS: ReadonlyArray<string> = NAV_SECTIONS.filter((s) => s.demo === true).map((s) => s.path)

/**
 * Sections to render: everything when demo mode is on (each demo one wearing a "Demo"
 * tag so nobody mistakes a stale screen for a current one), else current screens only.
 */
export function visibleNavSections(demoMode: boolean): ReadonlyArray<AppSidebarSection> {
  return NAV_SECTIONS.filter((section) => demoMode || section.demo !== true).map((section) =>
    section.demo === true ? { ...section, tag: "Demo" } : section,
  )
}
